import { useState, useEffect } from "react";
// ⚠️ Update the path to match your project structure
import {
  getAreas,
  getBranches,
  getJobs,
  getDepartments,
  getEmployees,
  getKpiNames,
  getKpiAssignments,
  getCompetencies,
  getCompetencyLevels, 
  getCompetencyToJobs,
  getJobGroups,getCompetencyToTraining,getAllLearningAndDevelopment
} from "services/api";

export const useImportDependencies = (dataType, locationState, authData) => {
  const [loading, setLoading] = useState(true);

  const [contextData, setContextData] = useState({
    models: [],
    areas: [],
    jobs: [],
    branches: [],
    departments: [],
    employees: [],
  });

  useEffect(() => {
    const loadDependencies = async () => {
      setLoading(true);

      // Initialize with available data
      const newData = {
        models: [],
        areas: authData?.areas || [],
        jobs: locationState?.jobs || authData?.jobs || [],
        branches: locationState?.empBranches || [],
        departments: locationState?.departments || [],
        employees: locationState?.employees || [],
        companyId: locationState?.companyId,
      };

      try {
        switch (dataType) {
          case "areas":
            newData.models = await fetchOrUse(null, getAreas);
            break;

          case "branches": {
            // Run both requests in parallel
            const [fetchedBranches, fetchedAreas] = await Promise.all([
              fetchOrUse(null, getBranches),
              fetchOrUse(newData.areas, getAreas),
            ]);
            newData.models = fetchedBranches;
            newData.areas = fetchedAreas;
            break;
          }

          case "jobs": {
            const fetchedJobs = await fetchOrUse(newData.jobs, getJobs);
            newData.models = fetchedJobs;
            newData.jobs = fetchedJobs;
            break;
          }

          case "departments": {
            const fetchedDepts = await fetchOrUse(newData.departments, getDepartments);
            newData.models = fetchedDepts;
            newData.departments = fetchedDepts;
            break;
          }

          case "employees": {
            // Fetch Branches, Jobs, and Employees simultaneously
            const [fetchedBranches, fetchedJobs, fetchedEmployees, fetchedDepts] =
              await Promise.all([
                fetchOrUse(newData.branches, getBranches),
                fetchOrUse(newData.jobs, getJobs),
                fetchOrUse(newData.employees, getEmployees),
                fetchOrUse(newData.departments, getDepartments),
              ]);

            newData.branches = fetchedBranches;
            newData.jobs = fetchedJobs;
            newData.models = fetchedEmployees;
            newData.departments = fetchedDepts;
            break;
          }

          case "kpis":
            newData.models = await fetchOrUse(locationState?.existingKpis, getKpiNames);
            break;

          case "scorecards": {
            // Fetch Assignments, Jobs, and Branches simultaneously
            const [fetchedAssignments, fetchedJobs, fetchedBranches, fetchedEmps, fetchedKpiNames] =
              await Promise.all([
                fetchOrUse(null, getKpiAssignments),
                fetchOrUse(newData.jobs, getJobs),
                fetchOrUse(newData.branches, getBranches),
                fetchOrUse(null, getEmployees),
                fetchOrUse(locationState?.existingKpis, getKpiNames),
              ]);

            newData.models = fetchedAssignments;
            newData.jobs = fetchedJobs;
            newData.branches = fetchedBranches;
            newData.employees = fetchedEmps;
            newData.kpiNames = fetchedKpiNames;
            break;
          }

          case "jobDetails": {
            // We need to fetch Jobs to resolve "Job Name" -> "Job ID"
            const fetchedJobs = await fetchOrUse(newData.jobs, getJobs);

            newData.jobs = fetchedJobs;
            // We set models to jobs so we can check if the job exists in the DB
            newData.models = fetchedJobs;
            break;
          }
          case "competencies": {
            // Fetch competencies and assign them to models
            newData.models = await fetchOrUse(null, getCompetencies);
            break;
          }

          case "competencyLevels": {
            // Run both requests in parallel
            const [fetchedCompetencies, fetchedCompetencyLevels] = await Promise.all([
              fetchOrUse(null, getCompetencies),
              fetchOrUse(null, getCompetencyLevels),
            ]);
            newData.competencies = fetchedCompetencies;
            newData.models = fetchedCompetencyLevels;
            break;
          }

          case "competencyToJob": {
            // Run both requests in parallel
            const [
              fetchedCompetencies,
              fetchedCompetencyLevels,
              fetchedCompetenciesToJob,
              fetchedJobs,
              fetchedJobGroups,
            ] = await Promise.all([
              fetchOrUse(null, getCompetencies),
              fetchOrUse(null, getCompetencyLevels),
              fetchOrUse(null, getCompetencyToJobs),
              fetchOrUse(null, getJobs),
              fetchOrUse(null, getJobGroups),
            ]);

            newData.models = fetchedCompetenciesToJob;
            newData.competencies = fetchedCompetencies;
            newData.competencyLevels = fetchedCompetencyLevels;
            newData.jobs = fetchedJobs;
            newData.jobGroups = fetchedJobGroups;

            break;
          }

          case "competencyToTraining": {
            // Run both requests in parallel
            const [fetchedCompetencyToTraining, fetchedCompetencies] =
              await Promise.all([
                fetchOrUse(null, getCompetencyToTraining),
                fetchOrUse(null, getCompetencies),
              ]);

            newData.models = fetchedCompetencyToTraining;
            newData.competencies = fetchedCompetencies;
            break;
          }

          case "learningAndDevelopment": {
            // Run both requests in parallel
            const [fetchedLearningAndDevelopment, fetchedCompetencies, fetchedCompToTraining, fetchedEmployees
              , fetchedLevels, fetchedCompetenciesToJob
            ] =
              await Promise.all([
                fetchOrUse(null, getAllLearningAndDevelopment),
                fetchOrUse(null, getCompetencies),
                fetchOrUse(null, getCompetencyToTraining),
                fetchOrUse(null, getEmployees),
                fetchOrUse(null, getCompetencyLevels),
                fetchOrUse(null, getCompetencyToJobs), 
              ]);

            newData.models = fetchedLearningAndDevelopment;
            newData.competencies = fetchedCompetencies;
            newData.compToTraining = fetchedCompToTraining;
            newData.employees = fetchedEmployees;
            newData.compLevels = fetchedLevels;
            newData.competenciesToJob = fetchedCompetenciesToJob;
            break;
          }


          default:
            break;
        }

        setContextData(newData);
      } catch (error) {
        console.error("Dependency Load Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDependencies();
  }, [dataType, locationState, authData]);

  return { loading, contextData };
};

// Helper: If existing data is present, return it. Otherwise, call the API.
async function fetchOrUse(existing, apiCall) {
  if (existing && existing.length > 0) return existing;
  const response = await apiCall();
  return response === "error" ? [] : response;
}
